import { ExternalLink } from "lucide-react";
import { Alert, DataTable, Icon, MarginBar, TableEmpty, TileGrid, buttonClass, type VizTone } from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useWhatsappQuery } from "../services/useWhatsapp";
import { Metric, NoRecords, QueryState, Section, StatusBadge, Timestamp } from "./Shared";
import { muted } from "./styles";
import { SearchSelect } from "@/shared/rd";
import { WaField } from "./WaField";
import { formatApproximateCost, providerLabel } from "../services/value-format";
const copy = keys.whatsapp;
export function BillingPanel({
  query,
  category,
  onCategory,
}: {
  query: string;
  category: string;
  onCategory: (value: string) => void;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const result = useWhatsappQuery("billing", query);
  const data = result.data;
  const amount = (value: number | null) =>
    formatApproximateCost(
      value,
      data?.currency ?? null,
      locale,
      t(copy.notAvailable),
    );
  const rows =
    data?.points
      .filter((p) => !category || p.category === category)
      .sort((a, b) => b.dateFrom.localeCompare(a.dateFrom)) ?? [];
  const totalCost =
    data?.byCategory.reduce((sum, row) => sum + (row.approximateCost ?? 0), 0) ?? 0;
  const tones: VizTone[] = ["blue", "ink", "purple", "good", "warn"];
  return (
    <QueryState loading={result.isLoading} error={result.error} retry={result.refetch} page={t(copy.billing).toLocaleLowerCase(locale)} rows={4}>
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          <p style={{ ...muted, color: "var(--rd-text-secondary)", fontSize: 14 }}>{t(copy.costHint)}</p>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <StatusBadge value={data.costAvailability} />
            <a
              href={data.billingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "var(--rd-primary)", textDecoration: "none" }}
            >
              {t(copy.metaBilling)}
              <Icon icon={ExternalLink} size={14} />
            </a>
          </div>
          {data.costAvailability !== "available" && (
            <Alert tone="warn" title={t(data.costAvailability === "partial" ? "whatsapp.costPartialTitle" : "whatsapp.costUnavailableTitle")}>
              {t(data.costAvailability === "partial" ? copy.partialCost : copy.unavailableCost)}
            </Alert>
          )}
          {data.points.length === 0 && data.byCategory.length === 0 ? (
            <section className="rd-card">
              <TableEmpty
                illustration="admin-billing"
                title={t("whatsapp.billingEmptyTitle")}
                actions={
                  <a
                    href={data.billingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={buttonClass("ghost", "sm")}
                    style={{ textDecoration: "none" }}
                  >
                    {t("whatsapp.openMetaBilling")}
                    <Icon icon={ExternalLink} size={14} />
                  </a>
                }
              >
                {t("whatsapp.billingEmptyText")}
              </TableEmpty>
            </section>
          ) : (
          <>
          <TileGrid min={250}>
            <Metric
              badge="currency"
              label={t(copy.approximateCost)}
              value={<bdi>{amount(data.approximateCost)}</bdi>}
              sub={
                <>
                  {t(copy.fetchedAt)}: <Timestamp value={data.fetchedAt} />
                </>
              }
            />
            <Metric
              badge="read"
              label={t(copy.volume)}
              value={data.volume === null ? t(copy.notAvailable) : data.volume.toLocaleString(locale)}
            />
          </TileGrid>
          <p style={muted}>
            {t(copy.dataThrough, { date: data.dataThrough ?? t(copy.notAvailable) })} {t(copy.reportingDelay)}
          </p>
          <Section title={t(copy.byCategory)} note={t(copy.approximateCost)}>
            {data.byCategory.length === 0 ? (
              <p style={muted}>{t(copy.empty)}</p>
            ) : (
              <div role="list" aria-label={t(copy.byCategory)} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {data.byCategory.map((row, index) => (
                  <div role="listitem" key={row.key}>
                    <MarginBar
                      label={<bdi>{providerLabel(row.key, t)}</bdi>}
                      note={`${t(copy.volume)}: ${row.volume.toLocaleString(locale)}`}
                      percent={totalCost > 0 && row.approximateCost !== null ? (row.approximateCost / totalCost) * 100 : 0}
                      value={<bdi>{amount(row.approximateCost)}</bdi>}
                      tone={tones[index % tones.length]}
                    />
                  </div>
                ))}
              </div>
            )}
          </Section>
          <Section title={t(copy.byDate)}>
            <div style={{ maxWidth: 280 }}>
              <WaField label={t(copy.category)}>
                {(id) => (
                <SearchSelect
                  id={id}
                  label={t(copy.category)}
                  emptyLabel={t(keys.common.noResults)}
                  value={category}
                  onChange={onCategory}
                  options={[
                    { value: "", label: t(copy.all) },
                    ...data.byCategory.map((c) => ({
                      value: c.key,
                      label: providerLabel(c.key, t),
                    })),
                  ]}
                />
                )}
              </WaField>
            </div>
            <DataTable
              label={t(copy.byDate)}
              rows={rows}
              rowKey={(r) => `${r.dateFrom}-${r.dateTo}-${r.category}-${r.pricingType}`}
              minWidth={640}
              empty={<NoRecords title={t("whatsapp.billingEmptyTitle")} text={t("whatsapp.billingEmptyText")} />}
              columns={[
                { key: "date", label: t(copy.date), render: (r) => <bdi className="rd-num">{r.dateFrom}</bdi> },
                { key: "category", label: t(copy.category), render: (r) => <bdi>{providerLabel(r.category, t)}</bdi> },
                { key: "type", label: t(copy.pricingType), render: (r) => <bdi>{providerLabel(r.pricingType, t)}</bdi> },
                { key: "volume", label: t(copy.volume), align: "end", numeric: true, render: (r) => r.volume.toLocaleString(locale) },
                { key: "cost", label: t(copy.approximateCost), align: "end", numeric: true, render: (r) => <bdi>{amount(r.approximateCost)}</bdi> },
              ]}
            />
          </Section>
          </>
          )}
        </div>
      )}
    </QueryState>
  );
}
